import React from 'react';
import Button from '@dashboard/components/Button';
import Link from '@dashboard/components/Link';
import { useAuth } from '@dashboard/context/AuthContext/AuthContext';
import getStripeConnectUrl from '@dashboard/services/getStripeConnectUrl';
import { Badge } from '@dashboard/components/ui/badge';
import checkIsConnectedToStripe from '@dashboard/services/seller/checkIsConnectedToStripe';
import SettingsHeader from '@dashboard/components/SettingsHeader';
import SettingsDivider from '@dashboard/components/SettingsDivider';
import CopyElement from '@dashboard/components/CopyElement';
import getStripeAccount from '@dashboard/lib/stripe/get-stripe-account';
import getStripeDashboardUrl from '@dashboard/lib/stripe/get-stripe-dashboard-url';
import getStripeAccountName from '@dashboard/lib/stripe/get-stripe-account-name';
import PropertyList from '@dashboard/components/PropertyList/PropertyList';
import SettingsContainer from '@/dashboard/components/SettingsContainer';
import PageHeader from '@dashboard/components/PageHeader';

const SettingsStripe = () => {
  const { seller } = useAuth();

  const stripeAccount = getStripeAccount(seller);
  const stripeAccountName = getStripeAccountName(seller);

  return (
    <div className="p-4 space-y-4">
      <PageHeader
        title="Stripe & payouts"
        description={
          <>
            Connect Checkout Page to Stripe account for secure and encrypted payment processing.{' '}
            <Link href="/docs/articles/connect-with-stripe" color="default" blank>
              Learn more
            </Link>
          </>
        }
      />

      <div className="mb-32">
        <SettingsContainer size="lg">
          {checkIsConnectedToStripe(seller) && (
            <>
              <div className="mb-4 text-sm">
                <PropertyList.Item>
                  <PropertyList.ItemLabel>Stripe Connect Status</PropertyList.ItemLabel>
                  <PropertyList.ItemValue>
                    <Badge variant="success">Connected</Badge>
                  </PropertyList.ItemValue>
                </PropertyList.Item>

                <PropertyList.Item>
                  <PropertyList.ItemLabel>Connected Account</PropertyList.ItemLabel>
                  <PropertyList.ItemValue>{stripeAccountName}</PropertyList.ItemValue>
                </PropertyList.Item>

                <PropertyList.Item>
                  <PropertyList.ItemLabel>Connected Account ID</PropertyList.ItemLabel>
                  <PropertyList.ItemValue>
                    <CopyElement text={stripeAccount} />
                  </PropertyList.ItemValue>
                </PropertyList.Item>
              </div>

              <div className="mb-8 flex flex-wrap gap-2">
                <Button
                  href={getStripeDashboardUrl('', { seller })}
                  target="_blank"
                  appearance="secondary"
                  external
                >
                  Stripe Dashboard
                </Button>
                <Button
                  to={getStripeDashboardUrl('/balance/overview', { seller })}
                  target="_blank"
                  appearance="secondary"
                  external
                >
                  Stripe account balance
                </Button>
              </div>

              <p className="text-gray-600 dark:text-muted-foreground text-sm">
                Want to connect a different Stripe account or disconnect?{' '}
                <Link
                  to={getStripeDashboardUrl('/account/applications', { seller })}
                  target="_blank"
                  className="inline-block underline"
                  external
                >
                  Revoke access
                </Link>
              </p>
            </>
          )}

          {!checkIsConnectedToStripe(seller) && (
            <Button to={getStripeConnectUrl()} appearance="primary">
              Connect with Stripe
            </Button>
          )}
        </SettingsContainer>

        <SettingsContainer size="lg">
          <SettingsDivider />
        </SettingsContainer>

        <SettingsContainer size="lg">
          <SettingsHeader id="payouts">Payouts</SettingsHeader>

          <div className="mb-4 text-sm">
            Understand how payouts work & how to make changes to your payout schedule:
          </div>
          <ul className="list-inside list-disc flex flex-col gap-y-2 mb-8 text-sm">
            <li>
              All payments you receive are held in your Stripe account. Checkout Page does not hold
              your funds.
            </li>
            <li>For you to receive funds, Stripe makes payouts to your bank account.</li>
            <li>
              If you're new to Stripe, you will receive your first payout 7-14 days after making
              your first sale.
            </li>
            <li>In most countries, your default payout schedule is daily automatic.</li>
            <li>
              You can change this to <i>weekly automatic</i>, <i>monthly automatic</i>, or{' '}
              <i>manual payouts</i>.
            </li>
          </ul>
          <p className="text-gray-700 dark:text-muted-foreground mb-4 text-sm">
            <Link
              to={getStripeDashboardUrl('/settings/payouts', { seller })}
              target="_blank"
              className="underline"
              external
            >
              Manage your bank account details & payouts schedule
            </Link>
          </p>

          <p className="text-gray-700 dark:text-muted-foreground text-sm">
            <Link
              to="https://stripe.com/docs/payouts#payout-schedule"
              target="_blank"
              className="underline"
              external
            >
              Learn more about receiving payouts
            </Link>
          </p>
        </SettingsContainer>
      </div>
    </div>
  );
};

export default SettingsStripe;
